import { html, LitElement, TemplateResult, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

// See https://vitejs.dev/guide/features#disabling-css-injection-into-the-page for information about ?inline present here
import innerStyles from './nuvance-content-fetch.inner.pcss?inline';
import globalStyles from './nuvance-content-fetch.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

/**
 * NuvanceContentFetch
 * This component provides a lightweight way to fetch and display remote content without page refreshes.
 * It asynchronously loads content from a specified path, extracts the relevant content using a CSS selector,
 * and renders it within the component's light DOM.
 *
 * @element nuvance-content-fetch
 * @slot - Default slot for fetched content*
 * @attribute {string} path - The path to fetch content from. Must be a relative path (e.g., "/api/content").
 *                            For security reasons, only content from the current domain is allowed.
 * @attribute {string} content-selector - CSS selector to extract specific content from the response.
 * @attribute {boolean} debug - When true, enables detailed logging to the console.
 * @attribute {boolean} isloading - Read-only attribute reflecting the loading state (for CSS targeting).
 *
 * @state loading - Component is fetching content (shows loading spinner)
 * @state no-results - Component found no matching content (shows "No content available" message)
 * @state has-content - Component successfully loaded and rendered content
 *
 * Features:
 * - Asynchronous content fetching with loading indicators
 * - Simple API with just two required attributes (path and content-selector)
 * - Secure by design - only allows content from current domain
 * - Loading spinner during content fetch
 * - "No content" state when selector doesn't match or content is empty
 * - Debug mode for development and troubleshooting
 * - Responsive to attribute changes with debounced refetching
 *
 * Usage:
 * ```html
 * <nuvance-content-fetch
 *   path="/api/content/endpoint"
 *   content-selector=".specific-content-class">
 * </nuvance-content-fetch>
 * ```
 *
 * Example Kyruus API endpoint (works):
 *  * /api/kyruus/provider-meet-the-team?sort=distance&location=41.411181%2C-72.9279&distance=25&limit=10
 */
@customElement('nuvance-content-fetch')
export class NuvanceContentFetch extends LitElement {
  // AdoptedStyleSheets controller for managing component styles
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  /**
   * The path to fetch content from
   */
  @property({ type: String, reflect: true })
  set path(value: string) {
    const oldValue = this._path;
    this._path = value;
    this.requestUpdate('path', oldValue);
    if (oldValue !== value && this.isConnected) {
      this.handleAttributeChange();
    }
  }

  get path(): string {
    return this._path;
  }

  private _path = '';

  /**
   * The content selector to grab content from the response
   */
  @property({ type: String, reflect: true, attribute: 'content-selector' })
  set contentSelector(value: string) {
    const oldValue = this._contentSelector;
    this._contentSelector = value;
    this.requestUpdate('contentSelector', oldValue);
    if (oldValue !== value && this.isConnected) {
      this.handleAttributeChange();
    }
  }

  get contentSelector(): string {
    return this._contentSelector;
  }

  private _contentSelector = '';

  /**
   * Debug mode flag that enables logging of component lifecycle events and state changes.
   */
  @property({ type: Boolean, attribute: 'debug' })
  debug = false;

  /**
   * Loading state that is reflected to an attribute for CSS targeting
   */
  @property({ type: Boolean, reflect: true, attribute: 'isloading' })
  isLoading = false;

  /**
   * Internal state to track if there are no results
   */
  @state()
  private hasNoResults = false;

  /**
   * Container element for the fetched content in the light DOM
   */
  private contentContainer: HTMLElement | null = null;

  /**
   * AbortController instance to cancel in-flight fetch requests
   */
  private abortController: AbortController | null = null;

  /**
   * Debounce timer for handling attribute changes
   */
  private debounceTimer: number | null = null;

  /**
   * Utility method for debug logging
   * Only logs when debug mode is enabled
   */
  private debugLog(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[nuvance-content-fetch] ${message}`, ...args);
    }
  }

  /**
   * Component lifecycle callback when connected to DOM
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.debugLog('Component connected to DOM');

    // Set loading state immediately
    this.isLoading = true;

    // Start async initialization
    this.initializeAsync().catch(error => {
      this.debugLog('Error during async initialization', error);
      // Make sure to reset loading state if initialization fails
      this.isLoading = false;
    });
  }

  /**
   * Component lifecycle callback when disconnected from DOM
   */
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.debugLog('Component disconnected from DOM');

    // Cancel any in-flight fetch requests
    this.cancelCurrentRequest();

    // Clear any pending debounce timers
    if (this.debounceTimer !== null) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /**
   * Handles attribute changes with debounce
   */
  private handleAttributeChange(): void {
    this.debugLog('Attribute changed, scheduling refetch');

    // Cancel any pending debounce timers
    if (this.debounceTimer !== null) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Set a new debounce timer
    this.debounceTimer = window.setTimeout(() => {
      this.debugLog('Debounce timer expired, executing refetch');
      this.refetchContent();
      this.debounceTimer = null;
    }, 100); // 100ms debounce
  }

  /**
   * Cancels the current in-flight fetch request if any
   */
  private cancelCurrentRequest(): void {
    if (this.abortController) {
      this.debugLog('Cancelling in-flight fetch request');
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Refetches content after attribute changes
   */
  private refetchContent(): void {
    this.debugLog('Refetching content after attribute change');

    // Cancel any in-flight fetch requests
    this.cancelCurrentRequest();

    // Clear current content
    this.clearContent();

    // Set loading state
    this.isLoading = true;

    // Reset no results state
    this.hasNoResults = false;

    // Fetch new content
    this.fetchContent().catch(error => {
      this.debugLog('Error during content refetch', error);
      this.isLoading = false;
      this.hasNoResults = true;
    });
  }

  /**
   * Asynchronously initializes the component
   */
  private async initializeAsync(): Promise<void> {
    this.debugLog('Starting async initialization');

    // Set up the content container
    await this.setupContentContainerAsync();

    // Fetch initial content
    await this.fetchContent();
  }

  /**
   * Asynchronously sets up the content container
   */
  private async setupContentContainerAsync(): Promise<void> {
    return new Promise(resolve => {
      // Use requestAnimationFrame to defer content container setup
      requestAnimationFrame(() => {
        this.setupContentContainer();
        resolve();
      });
    });
  }

  /**
   * Sets up the content container in the light DOM
   */
  private setupContentContainer(): void {
    // Check if the container already exists
    this.contentContainer = this.querySelector('.content-container');

    if (!this.contentContainer) {
      // Create a new container if it doesn't exist
      this.contentContainer = document.createElement('div');
      this.contentContainer.className = 'content-container';
      this.appendChild(this.contentContainer);
      this.debugLog('Created new content container in light DOM');
    } else {
      this.debugLog('Using existing content container in light DOM');
    }
  }

  /**
   * Fetches content from the specified path
   */
  private async fetchContent(): Promise<void> {
    if (!this.path || !this.contentContainer) {
      this.debugLog('Cannot fetch content - missing path or container', {
        path: this.path,
        contentContainer: !!this.contentContainer,
      });
      // Update loading and result states when missing required attributes
      this.isLoading = false;
      this.hasNoResults = true;
      return;
    }

    if (!this.contentSelector) {
      this.debugLog('Cannot fetch content - missing content selector', {
        selector: this.contentSelector,
      });
      // Update loading and result states when missing required attributes
      this.isLoading = false;
      this.hasNoResults = true;
      return;
    }

    this.debugLog('Fetching content', {
      path: this.path,
      selector: this.contentSelector,
    });

    try {
      // Create a new AbortController for this request
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      // Construct the URL with the path
      let url: URL;

      if (this.path.startsWith('http')) {
        // Full URL - check if it's from the current domain
        url = new URL(this.path);

        // Only allow requests to the current domain for security
        if (url.hostname !== window.location.hostname) {
          this.debugLog(
            'Fetch aborted - security restriction: only current domain is allowed',
            {
              requestedDomain: url.hostname,
              currentDomain: window.location.hostname,
            }
          );
          throw new Error(
            `Security restriction: Only the current domain (${window.location.hostname}) is allowed`
          );
        }

        this.debugLog('Using full URL from path', {
          url: url.toString(),
        });
      } else {
        // Relative path - combine with origin
        const path = this.path.startsWith('/') ? this.path : `/${this.path}`;
        url = new URL(path, window.location.origin);
        this.debugLog('Using relative path', {
          path: this.path,
          url: url.toString(),
        });
      }

      // Fetch the content with AbortController signal
      const response = await fetch(url.toString(), { signal });

      if (!response.ok) {
        this.debugLog('Fetch failed', {
          status: response.status,
          statusText: response.statusText,
          url: url.toString(),
        });
        throw new Error(`Failed to fetch content: ${response.statusText}`);
      }

      this.debugLog('Received response', {
        status: response.status,
        url: url.toString(),
      });

      const html = await response.text();
      this.parseAndSetContent(html);

      // Only set loading to false when the request completes successfully
      this.isLoading = false;
    } catch (error) {
      // Check if the request was aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        this.debugLog('Fetch aborted by user or component');
        // Don't update state for aborted requests since a new request will follow
        return;
      }

      this.debugLog('Error fetching content', error);
      this.clearContent();
      this.hasNoResults = true;
      this.isLoading = false;
    } finally {
      // Only clean up the AbortController without affecting loading state
      if (this.abortController) {
        this.abortController = null;
        this.debugLog('Fetch completed', {
          isLoading: this.isLoading,
          hasNoResults: this.hasNoResults,
        });
      }
    }
  }

  /**
   * Parses the HTML response and extracts the relevant content
   */
  private parseAndSetContent(html: string): void {
    if (!this.contentContainer) {
      return;
    }

    // Create a temporary element to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find the content using the selector
    const content = doc.querySelector(this.contentSelector);

    if (!content) {
      this.debugLog(
        `Content selector "${this.contentSelector}" not found in response`
      );
      this.clearContent();
      this.hasNoResults = true;
      return;
    }

    // Clear existing content
    this.clearContent();

    // Add the new content
    this.contentContainer.innerHTML = content.innerHTML;
    this.hasNoResults = this.contentContainer.innerHTML.trim() === '';

    this.debugLog('Content updated', {
      hasContent: !this.hasNoResults,
      contentLength: this.contentContainer.innerHTML.length,
    });
  }

  /**
   * Clears the content container
   */
  private clearContent(): void {
    if (this.contentContainer) {
      this.debugLog('Clearing content container', {
        childCount: this.contentContainer.childNodes.length,
      });

      while (this.contentContainer.firstChild) {
        this.contentContainer.removeChild(this.contentContainer.firstChild);
      }
    }
  }

  /**
   * Renders the component
   */
  render(): TemplateResult {
    this.debugLog('Rendering component', {
      isLoading: this.isLoading,
      hasNoResults: this.hasNoResults,
    });

    return html`
      ${this.isLoading
        ? html`<div class="loading-spinner" aria-live="polite" role="status">
            <span class="visually-hidden">Loading content...</span>
          </div>`
        : nothing}
      ${this.hasNoResults
        ? html`<div class="no-results">No content available</div>`
        : nothing}

      <slot></slot>
    `;
  }
}

// Type declaration for global HTML element map
declare global {
  interface HTMLElementTagNameMap {
    'nuvance-content-fetch': NuvanceContentFetch;
  }
}
